import { create } from 'zustand'
import type {
  ATMDevice,
  ATMEvent,
  ATMAlert,
  FleetKPIs,
  SimulationRun,
  ATMZone,
  ATMModel,
  ATMStatus,
} from '@atmvision/types'

export interface Filters {
  zone: ATMZone | 'ALL'
  model: ATMModel | 'ALL'
  status: ATMStatus | 'ALL'
  so_image: string | 'ALL'
  search: string
}

export interface ATMStore {
  fleet: ATMDevice[]
  events: ATMEvent[]
  alerts: ATMAlert[]
  kpis: FleetKPIs | null
  simulationRun: SimulationRun | null
  activeTab: string
  filters: Filters
  wsConnected: boolean
  theme: 'dark' | 'light'

  // actions
  setFleet(fleet: ATMDevice[]): void
  updateDeviceStatus(terminalId: string, status: ATMStatus): void
  addEvent(event: ATMEvent): void
  addEvents(events: ATMEvent[]): void
  setAlerts(alerts: ATMAlert[]): void
  addAlert(alert: ATMAlert): void
  updateAlert(alertId: string, patch: Partial<ATMAlert>): void
  updateKPIs(kpis: FleetKPIs): void
  setSimulationRun(run: SimulationRun): void
  setActiveTab(tab: string): void
  setFilters(filters: Partial<Filters>): void
  setWsConnected(connected: boolean): void
  clearEvents(): void
  toggleTheme(): void
}

const DEFAULT_FILTERS: Filters = {
  zone: 'ALL',
  model: 'ALL',
  status: 'ALL',
  so_image: 'ALL',
  search: '',
}

export const useATMStore = create<ATMStore>((set) => ({
  fleet: [],
  events: [],
  alerts: [],
  kpis: null,
  simulationRun: null,
  activeTab: 'dashboard',
  filters: DEFAULT_FILTERS,
  wsConnected: false,
  theme: 'dark',

  setFleet: (fleet) => set({ fleet }),

  updateDeviceStatus: (terminalId, status) =>
    set((state) => ({
      fleet: state.fleet.map((d) =>
        d.terminal_id === terminalId ? { ...d, status } : d
      ),
    })),

  addEvent: (event) =>
    set((state) => ({
      events: [event, ...state.events].slice(0, 2000),
    })),

  addEvents: (events) =>
    set((state) => ({
      events: [...events, ...state.events].slice(0, 2000),
    })),

  setAlerts: (alerts) => set({ alerts }),

  addAlert: (alert) =>
    set((state) => ({
      alerts: [alert, ...state.alerts].slice(0, 500),
    })),

  updateAlert: (alertId, patch) =>
    set((state) => ({
      alerts: state.alerts.map((a) =>
        a.alert_id === alertId ? { ...a, ...patch } : a
      ),
    })),

  updateKPIs: (kpis) => set({ kpis }),

  setSimulationRun: (run) => set({ simulationRun: run }),

  setActiveTab: (tab) => set({ activeTab: tab }),

  setFilters: (filters) =>
    set((state) => ({ filters: { ...state.filters, ...filters } })),

  setWsConnected: (connected) => set({ wsConnected: connected }),

  clearEvents: () => set({ events: [] }),

  toggleTheme: () =>
    set((state) => ({ theme: state.theme === 'dark' ? 'light' : 'dark' })),
}))
